import { useState, useEffect, useCallback, useContext, useRef } from 'react'
import { useSelector, useDispatch, shallowEqual } from 'react-redux'
import { createUseStyles } from 'react-jss'
import { Context as GeneralContext } from '@/context'
import Meta from '@/components/Meta'
import LocomotiveScroll from '@/components/LocomotiveScroll'
import Hero from '@/components/Hero'
import Footer from '@/components/Footer'
import useHandleAllMediaWithCb from '@/hooks/useHandleAllMediaWithCb'
import { cutExcerpt, insertBrAfterFirstSpace } from '@/utils/strings'
import actions from '@/actions'
import style from './style'

const useStyles = createUseStyles(style)

const ArchiveCaseStudies = ({
  cpt: cptFromProps,
  stub,
}) => {
  const { getImageUrl, setPageAnimationReady, setHeaderInverse } = useContext(GeneralContext)
  const [load, setLoad] = useState(false)
  const classes = useStyles()
  const $root = useRef()
  const [cases, setCases] = useState([])

  /*------------------------------
  Executes a callback on loading each image
  ------------------------------*/
  useHandleAllMediaWithCb({
    init: load,
    ref: $root.current?.ref,
  })

  /*------------------------------
  Redux Connect
  ------------------------------*/
  const { current, page, archiveCases, pages, strings } = useSelector((state) => ({
    current: state.page.current,
    archiveCases: state.page.archive[cptFromProps] || [],
    pages: state.page.pages,
    mediaQueryKey: state.mediaQuery.key,
    ready: state.loading.ready,
    strings: state.language.string,
    page: state.page.pages[stub] || {},
  }), shallowEqual)

  /*------------------------------
  Redux Actions
  ------------------------------*/
  const dispatch = useDispatch()
  const getPage = useCallback((index) => dispatch(actions.page.getDataPage(index)), [dispatch])
  const getCPT = useCallback((cpt, index) => dispatch(actions.page.getListCPT(cpt, index)), [dispatch])
  const setCurrentPage = useCallback((index) => dispatch(actions.page.setCurrent(index)), [dispatch])
  const updatePageReady = useCallback((index) => dispatch(actions.loading.setReady(index)), [dispatch])
  const setSiteLoaded = useCallback(() => dispatch(actions.loading.setSiteLoaded()), [dispatch])

  /*------------------------------
  Complete Preload
  ------------------------------*/
  const completePreload = () => {
    updatePageReady(true)
    setSiteLoaded()
    setLoad(true)
  }

  /*------------------------------
  Initialize (API Calls)
  ------------------------------*/
  const initialize = () => {
    if (current !== stub) setCurrentPage(stub, 'pages')
    getPage(stub)
    getCPT(cptFromProps)
  }

  /*------------------------------
  If there are products & page is loaded from redux, complete the preload
  ------------------------------*/
  useEffect(() => {
    if (cases.length && Object.keys(page).length > 0) completePreload()
  }, [page, cases])

  /*------------------------------
  Map ArchiveCases to Cases
  ------------------------------*/
  useEffect(() => {
    if (archiveCases.length) {
      const casesMapped = archiveCases.map((c) => {
        const p = pages[c]
        if (p) {
          return {
            id: p.id,
            title: `${p.title.rendered}<br />${p.location}`,
            text: cutExcerpt(p.content.rendered),
            image: getImageUrl(p.featured_media, 'large', p.slug),
            link: p.link,
          }
        }
        return null
      })
      setCases(casesMapped)
    }
  }, [archiveCases])

  /*------------------------------
  Did Mount
  ------------------------------*/
  useEffect(() => {
    if (!load) initialize()
  }, [])

  /*------------------------------
  Set Header Logo Styles
  ------------------------------*/
  useEffect(() => {
    setHeaderInverse(true)
    return () => setHeaderInverse(false)
  }, [])

  /*------------------------------
  Render Helmet
  ------------------------------*/
  const renderHelmet = () => {
    return load && (
      <Meta head_json={page.yoast_head} />
    )
  }

  /*------------------------------
  Render Heading
  ------------------------------*/
  const renderHeading = () => {
    return (
      <Hero
        aboveTheFold
        viewportHeight={false}
        background="light"
        fontSize="lg"
        text={cutExcerpt(page.content.rendered)}
        title={insertBrAfterFirstSpace(page.title.rendered)}
        imgSize="crop"
        textFormat={true}
        onComplete={() => setPageAnimationReady(true)}
      />
    )
  }

  /*------------------------------
  Render Cases
  ------------------------------*/
  const renderCases = () => {
    return Object.keys(cases).length > 0 && cases.map((item, ind) => (
      <Hero
        key={ind.toString()}
        borderTitle
        fontSize="sm"
        img={item.image}
        link={item.link}
        pretitle={strings.case_studies_label}
        text={item.text}
        title={item.title}
      />
    ))
  }

  /*------------------------------
  Render Footer
  ------------------------------*/
  const renderFooter = () => {
    return <Footer />
  }

  /*------------------------------
  Render Content
  ------------------------------*/
  const renderContent = () => {
    return load && (
      <>
        {renderHeading()}
        {renderCases()}
        {renderFooter()}
      </>
    )
  }

  return (
    <LocomotiveScroll
      init={load}
      className={`page pageArchiveCaseStudies ${classes.root}`}
      ref={$root}
    >
      {renderHelmet()}
      {renderContent()}
    </LocomotiveScroll>
  )
}

export default ArchiveCaseStudies
